import { useEffect, useState } from 'react'
import { ArrowRightLeft, AlertTriangle, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import {
  getPendingTransfer,
  initiateTransfer,
  cancelTransfer,
  type App,
  type AppTransfer,
} from '../lib/api'
import { StyledSelect } from './ui/StyledSelect'

interface TransferOwnershipSectionProps {
  app: App
  /** Owner or platform admin — only they may transfer. Section renders nothing otherwise. */
  canTransfer: boolean
  /** Called after a transfer is initiated/cancelled so the parent can refresh. */
  onChanged?: () => void
  /** Start expanded (e.g. when embedded on a page rather than a modal). */
  defaultOpen?: boolean
}

/**
 * Collapsible "Transfer ownership" control, shared by the Share modal and the
 * App Details sharing section so an owner can hand the app off from either place.
 * Recipient-accept flow: initiate → they accept (surfaced elsewhere via banner +
 * notification) → ownership moves.
 */
export default function TransferOwnershipSection({
  app,
  canTransfer,
  onChanged,
  defaultOpen = false,
}: TransferOwnershipSectionProps) {
    const [open, setOpen] = useState(defaultOpen)
    const [pending, setPending] = useState<AppTransfer | null>(null)
    const [email, setEmail] = useState('')
    // Default to keeping editor access — matches Google Workspace, where making
    // someone else the owner leaves you as an editor rather than removing you.
    const [retain, setRetain] = useState('edit') // 'none' | 'view' | 'edit'
    const [busy, setBusy] = useState(false)
    const [confirm, setConfirm] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
      if (!canTransfer) return
      loadPending()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [app.name])

    async function loadPending() {
      try {
        const transfer = await getPendingTransfer(app.name)
        setPending(transfer)
        // Auto-expand so an existing pending transfer isn't hidden behind the toggle.
        if (transfer) setOpen(true)
      } catch {
        // non-owners get 403; silently ignore
      }
    }

    async function handleInitiate() {
      setBusy(true)
      setError(null)
      try {
        const transfer = await initiateTransfer(app.name, email.trim(), retain === 'none' ? null : retain)
        setPending(transfer)
        setEmail('')
        setConfirm(false)
        onChanged?.()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start transfer')
      } finally {
        setBusy(false)
      }
    }

    async function handleCancel() {
      setBusy(true)
      setError(null)
      try {
        await cancelTransfer(app.name)
        setPending(null)
        onChanged?.()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to cancel transfer')
      } finally {
        setBusy(false)
      }
    }

    if (!canTransfer) return null

    return (
      <div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-2 text-left"
          aria-expanded={open}
        >
          {open ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
          )}
          <ArrowRightLeft className="w-4 h-4 text-muted-foreground shrink-0" />
          <span className="text-sm font-medium">Transfer ownership</span>
          {pending && !open && (
            <span className="ml-auto text-xs text-amber-600 dark:text-amber-400">Pending</span>
          )}
        </button>

        {open && (
          <div className="mt-3">
            <p className="text-xs text-muted-foreground mb-3">
              Hand this app to another registered user. They must accept before ownership moves. You can
              give it away completely or keep access as a collaborator.
            </p>

            {pending ? (
              <div className="flex items-center justify-between gap-2 p-3 rounded-lg bg-amber-500/10">
                <div className="min-w-0 flex-1 text-sm">
                  <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Awaiting acceptance
                  </span>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">
                    Offered to {pending.to_email}
                    {pending.retain_role
                      ? ` · you keep ${pending.retain_role} access`
                      : ' · you give it away fully'}
                  </div>
                </div>
                <button
                  onClick={handleCancel}
                  disabled={busy}
                  className="px-3 py-1.5 rounded-lg border border-border hover:bg-secondary text-sm shrink-0 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel'}
                </button>
              </div>
            ) : confirm ? (
              <div className="p-3 rounded-lg border border-amber-500/40 bg-amber-500/10">
                <div className="flex items-start gap-2 text-sm mb-3">
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <span>
                    Transfer ownership of <strong>{app.display_name || app.name}</strong> to{' '}
                    <strong className="break-all">{email.trim()}</strong>? Once they accept,{' '}
                    {retain === 'none'
                      ? 'you will lose all access to this app.'
                      : `you will keep ${retain} access as a collaborator.`}
                  </span>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setConfirm(false)}
                    disabled={busy}
                    className="px-3 py-1.5 rounded-lg border border-border hover:bg-secondary text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleInitiate}
                    disabled={busy}
                    className="px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-600/90 disabled:opacity-50 text-sm inline-flex items-center gap-1.5"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Confirm transfer
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">New owner&apos;s email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="new-owner@example.com"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Your access after transfer</label>
                  <StyledSelect
                    value={retain}
                    onChange={setRetain}
                    options={[
                      { value: 'none', label: 'No access (give away)' },
                      { value: 'view', label: 'Keep as Viewer' },
                      { value: 'edit', label: 'Keep as Editor' },
                    ]}
                    className="w-full justify-between"
                  />
                </div>
                <button
                  onClick={() => {
                    setError(null)
                    setConfirm(true)
                  }}
                  disabled={!email.trim()}
                  className="w-full px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-600/90 disabled:opacity-50 text-sm font-medium"
                >
                  Transfer ownership
                </button>
              </div>
            )}

            {error && (
              <div className="mt-2 p-2 rounded-lg bg-destructive/10 text-destructive text-xs">{error}</div>
            )}
          </div>
        )}
      </div>
    )
}
